import * as React from "react";
import {useEffect, useRef, useState} from "react";
import {Button, Dropdown, Header, Icon, Input, Label, Message, Modal, Segment} from "semantic-ui-react";
import AceEditor from "react-ace";
import "ace-builds/src-noconflict/mode-json";
import "ace-builds/src-noconflict/theme-monokai";
import DiffViewer from "react-diff-viewer";
import {AiParseResult, continueAiChat, runSingleAiRequest} from "./aiRequestsApi";
import {usePrettifier} from "./aiPrettify";

const RUN_COUNT_OPTIONS = [3, 5, 10];
const ORIGINAL = 0;
const DEFAULT_VISIBLE_RUNS = 3;

const KEY_ORIGINAL_OUTPUT = 'original-output';
const KEY_ORIGINAL_INPUT = 'original-input';
const keyForRun = (nr: number) => `run-${nr}`;

interface RerunRun {
    nr: number;
    status: 'running' | 'done' | 'error';
    output?: string;
    thinkingMessage?: string;
    promptTokens?: number;
    completionTokens?: number;
    price?: number;
    durationMs?: number;
    error?: string;
}

interface UsageStats {
    promptTokens?: number;
    completionTokens?: number;
    price?: number;
}

interface ChatMsg extends UsageStats {
    role: 'user' | 'assistant';
    content: string;
    thinking?: string;
    /** the initial prompt - rendered collapsed */
    isPrompt?: boolean;
}

export interface AiRequestRerunModalProps {
    open: boolean;
    onClose: () => void;
    source: AiParseResult | null;
    model: string;
    modelRecognized: boolean;
}

function copy(text: string) {
    navigator.clipboard?.writeText(text);
}

/* ------------------------------------------------------------------ */
/* chat panel - module level, so its state survives parent re-renders  */
/* ------------------------------------------------------------------ */

function RerunChatPanel(props: {
    title: string;
    model: string;
    prompt: string;
    answer: string;
    thinking?: string;
    seedStats?: UsageStats;
    onClose: () => void;
}) {
    const [messages, setMessages] = useState<ChatMsg[]>([
        {role: 'user', content: props.prompt, isPrompt: true},
        {
            role: 'assistant',
            content: props.answer,
            thinking: props.thinking,
            promptTokens: props.seedStats?.promptTokens,
            completionTokens: props.seedStats?.completionTokens,
            price: props.seedStats?.price
        }
    ]);
    const [input, setInput] = useState("");
    const [loading, setLoading] = useState(false);
    const [promptOpen, setPromptOpen] = useState(false);
    const endRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        endRef.current?.scrollIntoView({behavior: 'smooth'});
    }, [messages]);

    const totals = messages.reduce((acc, m) => ({
        in: acc.in + (m.promptTokens ?? 0),
        out: acc.out + (m.completionTokens ?? 0),
        price: acc.price + (m.price ?? 0)
    }), {in: 0, out: 0, price: 0});

    async function send() {
        if (!input.trim() || loading) return;
        const next: ChatMsg[] = [...messages, {role: 'user', content: input}];
        setMessages(next);
        setInput("");
        setLoading(true);
        try {
            const payload = next.map(m => (m.role === 'assistant' && m.thinking?.trim())
                ? {
                    role: m.role,
                    content: `<thinking_block_generated_for_this_message>\n${m.thinking.trim()}\n` +
                        `</thinking_block_generated_for_this_message>\n\n${m.content}`
                }
                : {role: m.role, content: m.content});

            const res = await continueAiChat(JSON.stringify(payload), props.model, {disableCache: true});
            setMessages([...next, {
                role: 'assistant',
                content: res.output,
                thinking: res.thinkingMessage,
                promptTokens: res.usageTokensNumber,
                completionTokens: res.completionTokensNumber,
                price: res.totalPromptPrice
            }]);
        } catch (e: any) {
            setMessages([...next, {role: 'assistant', content: `❌ ${e?.message || String(e)}`}]);
        } finally {
            setLoading(false);
        }
    }

    function drawUsage(m: ChatMsg) {
        if (m.promptTokens == null && m.completionTokens == null && m.price == null) return null;
        return <span style={{fontSize: '0.72em', color: '#777', display: 'inline-flex', gap: 6}}>
            {m.promptTokens != null && <span>in {m.promptTokens.toLocaleString()}</span>}
            {m.completionTokens != null && <span>out {m.completionTokens.toLocaleString()}</span>}
            {m.price != null && <span>${m.price.toFixed(4)}</span>}
        </span>;
    }

    return <div style={{
        display: 'flex', flexDirection: 'column', minHeight: 0, height: '100%',
        border: '1px solid #2185d0', borderRadius: 4, backgroundColor: '#fafcff'
    }}>
        <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '4px 8px', backgroundColor: '#eef5fd', borderBottom: '1px solid #d6e4f5'
        }}>
            <strong style={{fontSize: '0.8em'}}>
                <Icon name="comments"/> Chat – {props.title}
            </strong>
            <span style={{fontSize: '0.75em', color: '#666'}}>
                sum conversation: in {totals.in.toLocaleString()} · out {totals.out.toLocaleString()} ·{' '}
                ${totals.price.toFixed(4)}
                <Icon name="close" link style={{marginLeft: 8}} onClick={props.onClose}/>
            </span>
        </div>

        <div style={{flex: 1, minHeight: 0, overflow: 'auto', padding: 6}}>
            {messages.map((m, i) => {
                if (m.isPrompt) {
                    return <div key={i} style={{marginBottom: 6}}>
                        <Button size="mini" basic compact onClick={() => setPromptOpen(!promptOpen)}>
                            <Icon name={promptOpen ? 'chevron down' : 'chevron right'}/>
                            Prompt ({m.content.length.toLocaleString()} chars)
                        </Button>
                        <Button size="mini" basic compact icon="copy" onClick={() => copy(m.content)}/>
                        {promptOpen && <div style={{
                            marginTop: 4, padding: '6px 8px', maxHeight: 220, overflow: 'auto',
                            borderRadius: 4, fontSize: '0.8em', whiteSpace: 'pre-wrap',
                            fontFamily: 'monospace', backgroundColor: '#eef2f5', border: '1px solid #d7dde2'
                        }}>{m.content}</div>}
                    </div>;
                }
                return <div key={i} style={{
                    marginBottom: 6, padding: '6px 8px', borderRadius: 4,
                    backgroundColor: m.role === 'user' ? '#e3f2fd' : '#f1f8e9',
                    border: m.role === 'user' ? '1px solid #90caf9' : '1px solid #c5e1a5'
                }}>
                    <div style={{
                        display: 'flex', justifyContent: 'space-between',
                        alignItems: 'center', gap: 6, marginBottom: 3
                    }}>
                        <strong style={{fontSize: '0.72em', color: m.role === 'user' ? '#1976d2' : '#558b2f'}}>
                            {m.role.toUpperCase()}
                        </strong>
                        <span style={{display: 'inline-flex', gap: 6, alignItems: 'center'}}>
                            {drawUsage(m)}
                            <Icon name="copy" link style={{fontSize: '0.85em'}} onClick={() => copy(m.content)}/>
                        </span>
                    </div>
                    <div style={{fontSize: '0.82em', whiteSpace: 'pre-wrap', fontFamily: 'monospace'}}>
                        {m.content}
                    </div>
                </div>;
            })}
            <div ref={endRef}/>
        </div>

        <div style={{padding: 6, borderTop: '1px solid #d6e4f5'}}>
            <Input
                fluid
                size="mini"
                placeholder="Message…"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={(e: any) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        send();
                    }
                }}
                action={<Button primary size="mini" loading={loading}
                                disabled={!input.trim() || loading} onClick={send}>Send</Button>}
            />
        </div>
    </div>;
}

export function AiRequestRerunModal(props: AiRequestRerunModalProps) {

    const {source, model, modelRecognized} = props;

    const [count, setCount] = useState<number>(RUN_COUNT_OPTIONS[0]);
    const [runs, setRuns] = useState<RerunRun[]>([]);
    const [running, setRunning] = useState<boolean>(false);
    const [hidden, setHidden] = useState<Set<number>>(new Set());
    const [chats, setChats] = useState<Set<number>>(new Set());
    const [thinkingOpen, setThinkingOpen] = useState<Set<number>>(new Set());
    const [prettify, setPrettify] = useState<boolean>(true);
    const [showSourceInput, setShowSourceInput] = useState<boolean>(false);
    const [diffMode, setDiffMode] = useState<boolean>(false);
    const [diffLeft, setDiffLeft] = useState<number>(ORIGINAL);
    const [diffRight, setDiffRight] = useState<number>(1);

    const {entries, ensure, reset} = usePrettifier();

    // fresh state whenever another request is opened
    useEffect(() => {
        setRuns([]);
        setHidden(new Set());
        setChats(new Set());
        setThinkingOpen(new Set());
        setDiffMode(false);
        setShowSourceInput(false);
        setDiffLeft(ORIGINAL);
        setDiffRight(1);
        reset();
    }, [source?._id, props.open, reset]);

    // ask the backend to format whatever is currently needed
    useEffect(() => {
        if (!prettify || !source) return;
        const output = source.rawOutput || source.output || "";
        if (output) ensure(KEY_ORIGINAL_OUTPUT, output);
        if (showSourceInput && source.input) ensure(KEY_ORIGINAL_INPUT, source.input);
        runs.forEach(r => {
            if (r.status === 'done' && r.output) ensure(keyForRun(r.nr), r.output);
        });
    }, [prettify, showSourceInput, runs, source, ensure]);

    const expanded = runs.length > 0;

    async function start() {
        if (!source || running || !modelRecognized) return;

        setRunning(true);
        reset();
        setRuns(Array.from({length: count}, (_, i) => ({nr: i + 1, status: 'running' as const})));
        setHidden(new Set(Array.from({length: count}, (_, i) => i + 1).filter(nr => nr > DEFAULT_VISIBLE_RUNS)));
        setChats(new Set());
        setDiffMode(false);
        setDiffRight(1);

        const patch = (nr: number, p: Partial<RerunRun>) =>
            setRuns(prev => prev.map(r => (r.nr === nr ? {...r, ...p} : r)));

        await Promise.all(Array.from({length: count}, async (_, i) => {
            const nr = i + 1;
            const started = Date.now();
            try {
                const res = await runSingleAiRequest(source.input, model, {disableCache: true});
                patch(nr, {
                    status: 'done',
                    output: res.output,
                    thinkingMessage: res.thinkingMessage,
                    promptTokens: res.promptTokens,
                    completionTokens: res.completionTokens,
                    price: res.price,
                    durationMs: Date.now() - started
                });
            } catch (e: any) {
                patch(nr, {status: 'error', error: e?.message || String(e), durationMs: Date.now() - started});
            }
        }));

        setRunning(false);
    }

    function labelOf(nr: number) {
        return nr === ORIGINAL ? "Original" : `Run #${nr}`;
    }

    function rawOf(nr: number): string {
        if (nr === ORIGINAL) {
            return showSourceInput
                ? (source?.input || "")
                : (source?.rawOutput || source?.output || "");
        }
        const run = runs.find(r => r.nr === nr);
        return run?.output ?? (run?.error ? `❌ ${run.error}` : "");
    }

    /** always the answer - used for diff, verdict and chat seed */
    function answerOf(nr: number): string {
        if (nr === ORIGINAL) return source?.rawOutput || source?.output || "";
        return runs.find(r => r.nr === nr)?.output || "";
    }

    function usageOf(nr: number): UsageStats {
        if (nr === ORIGINAL) {
            return {
                promptTokens: source?.promptTokens,
                completionTokens: source?.completionTokens,
                price: source?.totalPrice
            };
        }
        const run = runs.find(r => r.nr === nr);
        return {promptTokens: run?.promptTokens, completionTokens: run?.completionTokens, price: run?.price};
    }

    /** key of the currently displayed content of a column */
    function viewKeyOf(nr: number): string {
        if (nr === ORIGINAL) return showSourceInput ? KEY_ORIGINAL_INPUT : KEY_ORIGINAL_OUTPUT;
        return keyForRun(nr);
    }

    /** key of the answer of a column (never the input) */
    function answerKeyOf(nr: number): string {
        return nr === ORIGINAL ? KEY_ORIGINAL_OUTPUT : keyForRun(nr);
    }

    function textOf(nr: number): string {
        const raw = rawOf(nr);
        if (!prettify) return raw;
        const entry = entries[viewKeyOf(nr)];
        return (entry?.status === 'done' && entry.isJson && entry.text != null) ? entry.text : raw;
    }

    function diffTextOf(nr: number): string {
        const raw = answerOf(nr);
        if (!prettify) return raw;
        const entry = entries[answerKeyOf(nr)];
        return (entry?.status === 'done' && entry.isJson && entry.text != null) ? entry.text : raw;
    }

    /** backend supplied canonical form -> format independent comparison */
    function compactOf(nr: number): string {
        const entry = entries[answerKeyOf(nr)];
        if (entry?.status === 'done' && entry.isJson && entry.compact) return entry.compact;
        return (answerOf(nr) || "").trim();
    }

    const allColumns = [ORIGINAL, ...runs.map(r => r.nr)];
    const visibleColumns = allColumns.filter(nr => !hidden.has(nr));
    const selectableColumns = allColumns.filter(nr =>
        nr === ORIGINAL || runs.find(r => r.nr === nr)?.status === 'done');

    const doneRuns = runs.filter(r => r.status === 'done');
    const totals = doneRuns.reduce((acc, r) => ({
        in: acc.in + (r.promptTokens ?? 0),
        out: acc.out + (r.completionTokens ?? 0),
        price: acc.price + (r.price ?? 0)
    }), {in: 0, out: 0, price: 0});

    const formatting = Object.keys(entries).some(k => entries[k].status === 'loading');

    function toggleIn(set: Set<number>, setter: (s: Set<number>) => void, nr: number) {
        const next = new Set(set);
        next.has(nr) ? next.delete(nr) : next.add(nr);
        setter(next);
    }

    function drawStats(nr: number) {
        const {promptTokens, completionTokens, price} = usageOf(nr);
        const run = runs.find(r => r.nr === nr);

        return <span style={{fontSize: '0.75em', color: '#666', display: 'inline-flex', gap: 8}}>
            {promptTokens != null && <span>in {promptTokens.toLocaleString()}</span>}
            {completionTokens != null && <span>out {completionTokens.toLocaleString()}</span>}
            {price != null && <span>${price.toFixed(4)}</span>}
            {run?.durationMs != null && <span>{(run.durationMs / 1000).toFixed(1)}s</span>}
        </span>;
    }

    /** tells the user why nothing got formatted */
    function drawFormatHint(nr: number) {
        if (!prettify) return null;
        const entry = entries[viewKeyOf(nr)];
        if (!entry) return null;
        if (entry.status === 'loading') return <Icon name="spinner" loading title="formatting"/>;
        if (entry.status === 'error') return <Label size="mini" color="red">format failed</Label>;
        if (entry.status === 'done' && !entry.isJson) {
            return <Label size="mini" color="red">not JSON – can&apos;t prettify</Label>;
        }
        return null;
    }

    function drawVerdict(nr: number) {
        const run = runs.find(r => r.nr === nr);
        if (nr === ORIGINAL || run?.status !== 'done') return null;
        return compactOf(nr) === compactOf(ORIGINAL)
            ? <Label size="mini" color="green">identical</Label>
            : <Label size="mini" color="red">different</Label>;
    }

    function drawColumn(nr: number) {
        const run = runs.find(r => r.nr === nr);
        const isOriginal = nr === ORIGINAL;
        const chatVisible = chats.has(nr);
        const canChat = isOriginal ? !!answerOf(nr) : run?.status === 'done';
        const thinking = isOriginal ? source?.thinkingMessage : run?.thinkingMessage;
        const viewEntry = entries[viewKeyOf(nr)];
        const editorMode = prettify && viewEntry?.status === 'done' && viewEntry.isJson ? 'p' : 'r';

        return <div key={nr} style={{
            display: 'flex', flexDirection: 'column', minHeight: 0, minWidth: 0, overflow: 'hidden',
            border: isOriginal ? '2px solid #2185d0'
                : run?.status === 'error' ? '2px solid #db2828' : '1px solid #ccc',
            borderRadius: 6, padding: 8, backgroundColor: isOriginal ? '#f7fbff' : '#fff'
        }}>
            <div style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                gap: 6, marginBottom: 6, flexWrap: 'wrap', flexShrink: 0
            }}>
                <div style={{display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap'}}>
                    <strong style={{fontSize: '0.9em'}}>{labelOf(nr)}</strong>
                    {isOriginal && <Label size="mini" color="blue">cached</Label>}
                    {run?.status === 'running' && <Icon name="spinner" loading/>}
                    {drawVerdict(nr)}
                    {drawFormatHint(nr)}
                    {drawStats(nr)}
                </div>
                <div style={{display: 'flex', gap: 4, alignItems: 'center'}}>
                    {isOriginal && <Button.Group size="mini">
                        <Button active={!showSourceInput} onClick={() => setShowSourceInput(false)}>Output</Button>
                        <Button active={showSourceInput} onClick={() => setShowSourceInput(true)}>Input</Button>
                    </Button.Group>}
                    {thinking && <Button size="mini" basic icon="lightbulb outline" title="thinking"
                                         onClick={() => toggleIn(thinkingOpen, setThinkingOpen, nr)}/>}
                    <Button size="mini" basic={!chatVisible} color={chatVisible ? "blue" : undefined}
                            icon="comments" title="chat with this answer" disabled={!canChat}
                            onClick={() => toggleIn(chats, setChats, nr)}/>
                    <Button size="mini" basic icon="copy" onClick={() => copy(rawOf(nr))}/>
                    <Button size="mini" basic icon="close" title="hide column"
                            onClick={() => toggleIn(hidden, setHidden, nr)}/>
                </div>
            </div>

            <div style={{flex: chatVisible ? '1 1 50%' : '1 1 auto', minHeight: 0}}>
                <AceEditor
                    key={`rerun-editor-${nr}-${showSourceInput ? 'in' : 'out'}-${editorMode}`}
                    mode="json"
                    theme="monokai"
                    value={textOf(nr)}
                    readOnly
                    width="100%"
                    height="100%"
                    fontSize={13}
                    showPrintMargin={false}
                    wrapEnabled={false}          /* no wrapping -> horizontal + vertical scrollbars */
                    editorProps={{$blockScrolling: true}}
                    setOptions={{useWorker: false, showLineNumbers: true, tabSize: 2}}
                />
            </div>

            {thinking && thinkingOpen.has(nr) && <div style={{
                marginTop: 6, maxHeight: 180, overflow: 'auto', flexShrink: 0,
                backgroundColor: '#f3e5f5', border: '1px dashed #9c27b0',
                padding: '8px 10px', fontSize: '0.82em', whiteSpace: 'pre-wrap',
                fontFamily: 'monospace', color: '#222'
            }}>{thinking}</div>}

            {chatVisible && canChat && <div style={{flex: '1 1 50%', minHeight: 0, marginTop: 6}}>
                <RerunChatPanel
                    key={`chat-${source?._id}-${nr}`}
                    title={labelOf(nr)}
                    model={model}
                    prompt={source?.input || ""}
                    answer={answerOf(nr)}
                    thinking={thinking}
                    seedStats={usageOf(nr)}
                    onClose={() => toggleIn(chats, setChats, nr)}
                />
            </div>}
        </div>;
    }

    function drawDiff() {
        const options = selectableColumns.map(nr => ({key: nr, value: nr, text: labelOf(nr)}));

        const leftEntry = entries[answerKeyOf(diffLeft)];
        const rightEntry = entries[answerKeyOf(diffRight)];
        const leftJson = leftEntry?.status === 'done' && !!leftEntry.isJson;
        const rightJson = rightEntry?.status === 'done' && !!rightEntry.isJson;
        const bothJson = leftJson && rightJson;

        // both sides must share the same formatting, otherwise the diff is noise
        const usePretty = prettify && bothJson;
        const left = usePretty ? diffTextOf(diffLeft) : answerOf(diffLeft);
        const right = usePretty ? diffTextOf(diffRight) : answerOf(diffRight);
        const identical = compactOf(diffLeft) === compactOf(diffRight);

        return <div>
            <div style={{display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8, flexWrap: 'wrap'}}>
                <Dropdown selection compact options={options} value={diffLeft}
                          onChange={(_, d) => setDiffLeft(d.value as number)}/>
                <Icon name="exchange"/>
                <Dropdown selection compact options={options} value={diffRight}
                          onChange={(_, d) => setDiffRight(d.value as number)}/>
                {identical
                    ? <Label size="small" color="green"><Icon name="check"/> identical</Label>
                    : <Label size="small" color="red"><Icon name="exchange"/> different</Label>}
                {prettify && !bothJson && (
                    <Label size="mini" color="red">
                        <Icon name="warning sign"/>
                        {!leftJson && !rightJson
                            ? 'both sides are not JSON – diffing raw text'
                            : !leftJson
                                ? `${labelOf(diffLeft)} is not JSON – diffing raw text`
                                : `${labelOf(diffRight)} is not JSON – diffing raw text`}
                    </Label>
                )}
            </div>
            <div style={{fontSize: '12px', overflow: 'auto'}}>
                <DiffViewer oldValue={left} newValue={right} splitView={true}/>
            </div>
        </div>;
    }

    function drawBody() {
        if (!expanded) {
            return <Message
                info
                content={`Runs the exact same prompt ${count}× in parallel with the AI cache disabled, using ${model}.`}
            />;
        }
        if (diffMode) return drawDiff();
        if (visibleColumns.length === 0) {
            return <Message warning content={"All columns are hidden - enable at least one above."}/>;
        }
        return <div style={{
            flex: 1, minHeight: 0, display: 'grid',
            gridTemplateColumns: `repeat(${visibleColumns.length}, minmax(0, 1fr))`, gap: 10
        }}>
            {visibleColumns.map(nr => drawColumn(nr))}
        </div>;
    }

    if (!source) return null;

    /** editors need a fixed height, the diff grows with its content */
    const contentStyle: React.CSSProperties = !expanded
        ? {padding: '12px 14px'}
        : diffMode
            ? {padding: '12px 14px', maxHeight: 'calc(100vh - 140px)', overflow: 'auto'}
            : {padding: '12px 14px', height: 'calc(100vh - 140px)', display: 'flex', flexDirection: 'column'};

    return <Modal open={props.open} onClose={props.onClose} size={expanded ? "fullscreen" : "small"}>
        <Header style={{padding: '12px 20px', backgroundColor: '#2c3e50', color: '#fff', margin: 0}}>
            <Icon name="redo"/> Rerun prompt
            <Label color="blue" size="small" style={{marginLeft: 10}}>{source.model}</Label>
            <Label size="small" style={{marginLeft: 6}}>cache disabled</Label>
        </Header>

        <Modal.Content style={contentStyle}>

            <Segment style={{flexShrink: 0, padding: '10px 12px', marginBottom: 10}}>
                <div style={{display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap'}}>
                    <Dropdown
                        selection compact
                        disabled={running}
                        options={RUN_COUNT_OPTIONS.map(c => ({key: c, value: c, text: `${c} runs`}))}
                        value={count}
                        onChange={(_, d) => setCount(d.value as number)}
                    />
                    <Button primary loading={running} disabled={running || !modelRecognized} onClick={start}>
                        <Icon name="play"/> Run {count}× with {model}
                    </Button>

                    {expanded && <>
                        <Button.Group size="small">
                            <Button active={!diffMode} onClick={() => setDiffMode(false)}>Editors</Button>
                            <Button active={diffMode} onClick={() => setDiffMode(true)}
                                    disabled={selectableColumns.length < 2}>Diff</Button>
                        </Button.Group>
                        <Button size="small" basic={!prettify} color={prettify ? 'blue' : undefined}
                                loading={formatting} onClick={() => setPrettify(!prettify)}>
                            <Icon name="code"/> {prettify ? 'Unprettify' : 'Prettify'}
                        </Button>
                    </>}

                    <div style={{marginLeft: 'auto', fontSize: '0.85em', color: '#333'}}>
                        <strong>sum {doneRuns.length} rerun(s):</strong>{' '}
                        in {totals.in.toLocaleString()} · out {totals.out.toLocaleString()} ·{' '}
                        <strong>${totals.price.toFixed(4)}</strong>
                        <span style={{color: '#888'}}> (original excluded)</span>
                    </div>
                </div>

                {expanded && !diffMode && <div style={{
                    display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap', marginTop: 8
                }}>
                    <span style={{fontSize: '0.85em', color: '#666'}}>show:</span>
                    {allColumns.map(nr => <Button
                        key={nr}
                        size="mini"
                        compact
                        basic={hidden.has(nr)}
                        color={hidden.has(nr) ? undefined : (nr === ORIGINAL ? "blue" : "green")}
                        onClick={() => toggleIn(hidden, setHidden, nr)}
                    >{labelOf(nr)}</Button>)}
                </div>}

                {!modelRecognized && <Message
                    warning size="tiny" style={{marginTop: 8}}
                    content={`Model "${source.model}" is not a known ParseTextModel - rerun is disabled.`}
                />}
            </Segment>

            {drawBody()}
        </Modal.Content>

        <Modal.Actions>
            <Button onClick={props.onClose}>Close</Button>
        </Modal.Actions>
    </Modal>;
}